'use client';

import * as React from 'react';
import { CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  success?: string;
  hint?: string;
  showCount?: boolean;
  maxLength?: number;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      label,
      error,
      success,
      hint,
      showCount,
      maxLength,
      disabled,
      id,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const textareaId = id || generatedId;
    const [charCount, setCharCount] = React.useState(
      typeof value === 'string' ? value.length : 0
    );
    const hasError = !!error;
    const hasSuccess = !!success && !hasError;

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setCharCount(e.target.value.length);
      onChange?.(e);
    };

    return (
      <div className="w-full space-y-2">
        <div className="relative">
          <textarea
            id={textareaId}
            className={cn(
              'peer w-full min-h-[120px] px-4 pt-6 pb-3 text-sm',
              'bg-transparent text-[var(--text-primary)] placeholder-transparent',
              'border border-[var(--border-default)] rounded-card',
              'transition-all duration-200',
              'focus:outline-none focus:border-[var(--primary)]',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'resize-y',
              hasError && 'border-[var(--error)] bg-[var(--error)]/5 focus:border-[var(--error)]',
              hasSuccess && 'border-[var(--lime-500)] bg-[var(--lime-500)]/5 focus:border-[var(--lime-500)]',
              className
            )}
            placeholder=" "
            disabled={disabled}
            maxLength={maxLength}
            value={value}
            onChange={handleChange}
            ref={ref}
            {...props}
          />

          {label && (
            <label
              htmlFor={textareaId}
              className={cn(
                'absolute left-4 top-4 text-sm text-[var(--text-tertiary)] pointer-events-none',
                'transition-all duration-200 ease-out',
                'peer-focus:text-xs peer-focus:top-2 peer-focus:text-[var(--primary)]',
                'peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:top-2',
                hasError && 'text-[var(--error)] peer-focus:text-[var(--error)] peer-[:not(:placeholder-shown)]:text-[var(--error)]',
                hasSuccess && 'text-[var(--success)] peer-focus:text-[var(--success)] peer-[:not(:placeholder-shown)]:text-[var(--success)]'
              )}
            >
              {label}
            </label>
          )}
        </div>

        <div className="flex justify-between items-center px-1">
          <div className="flex items-center gap-1.5">
            {error && (
              <>
                <WarningCircle className="h-4 w-4 text-[var(--error)]" weight="fill" />
                <p className="text-xs font-medium text-[var(--error)]">{error}</p>
              </>
            )}
            {success && !error && (
              <>
                <CheckCircle className="h-4 w-4 text-[var(--success)]" weight="fill" />
                <p className="text-xs font-medium text-[var(--success)]">{success}</p>
              </>
            )}
            {hint && !error && !success && (
              <p className="text-xs text-[var(--text-tertiary)]">{hint}</p>
            )}
          </div>

          {showCount && maxLength && (
            <span
              className={cn(
                'text-xs text-[var(--text-tertiary)]',
                charCount >= maxLength && 'text-[var(--error)] font-medium'
              )}
            >
              {charCount}/{maxLength}
            </span>
          )}
        </div>
      </div>
    );
  }
);
Textarea.displayName = 'Textarea';

export { Textarea };
